import * as THREE from 'three';
import { Entity } from './Entity.js';
import { Experience } from './Experience.js';
import { Powerup } from './Powerup.js';

export class Bullet extends Entity {
    constructor(x, y, targetX, targetY, weaponType, isCritical = false, scene) {
        super(x, y, weaponType.char, weaponType.color, scene);
        
        this.direction = new THREE.Vector3(targetX - x, 0, targetY - y).normalize();
        this.speed = weaponType.projectileSpeed;
        this.damage = weaponType.damage * (isCritical ? 2.0 : 1); // Assuming player crit multiplier
        this.piercing = weaponType.piercing || 1;
        this.explosive = weaponType.explosive || false;
        this.explosionRadius = weaponType.explosionRadius || 3;
        this.lifetime = 5; // Increased for larger world
        this.isCritical = isCritical;
        
        if (isCritical) {
            this.glowIntensity = 2;
            this.scale = 1.3;
        }
    }
    
    update(deltaTime, camera, gameState) {
        super.update(deltaTime, camera);
        
        // Move bullet
        this.position.add(this.direction.clone().multiplyScalar(this.speed * deltaTime));
        this.lifetime -= deltaTime;
        
        // Check collision with enemies
        for (let i = gameState.enemies.length - 1; i >= 0; i--) {
            const enemy = gameState.enemies[i];
            if (this.isColliding(enemy)) {
                this.hitEnemy(enemy, gameState, i);
                
                this.piercing--;
                if (this.piercing <= 0) {
                    return true; // Bullet should be destroyed
                }
            }
        }
        
        return this.lifetime <= 0;
    }
    
    hitEnemy(enemy, gameState, enemyIndex) {
        const wasKilled = enemy.takeDamage(this.damage, this.isCritical);
        
        // Play hit sound
        if (window.gameAudio) {
            window.gameAudio.playHit();
        }
        
        if (wasKilled) {
            // Enemy killed
            this.spawnExperience(enemy, gameState);
            this.spawnPowerup(enemy, gameState);
            
            // Handle death effects (like splitting)
            enemy.onDeath(gameState);
            
            enemy.destroy();
            gameState.enemies.splice(enemyIndex, 1);
            gameState.stats.kills++;
        }
        
        if (this.explosive) {
            this.explode(gameState);
        }
    }
    
    explode(gameState) {
        // Play explosion sound
        if (window.gameAudio) {
            window.gameAudio.playExplosion();
        }
        
        // Damage nearby enemies
        gameState.enemies.forEach(enemy => {
            const dist = this.getDistanceTo(enemy);
            if (dist < this.explosionRadius) {
                const damage = this.damage * (1 - dist / this.explosionRadius);
                const wasKilled = enemy.takeDamage(damage);
                
                if (wasKilled) {
                    this.spawnExperience(enemy, gameState);
                    this.spawnPowerup(enemy, gameState);
                    enemy.onDeath(gameState);
                    
                    enemy.destroy();
                    const index = gameState.enemies.indexOf(enemy);
                    if (index > -1) {
                        gameState.enemies.splice(index, 1);
                        gameState.stats.kills++;
                    }
                }
                
                enemy.stun(0.5); // Stun briefly
            }
        });
        
        // Create explosion visual effect
        this.createExplosionEffect(gameState);
    }
    
    createExplosionEffect(gameState) {
        // Create a temporary explosion visual
        const explosionGeometry = new THREE.SphereGeometry(this.explosionRadius, 8, 8);
        const explosionMaterial = new THREE.MeshBasicMaterial({
            color: 0xff6600,
            transparent: true,
            opacity: 0.6,
            wireframe: true
        });
        
        const explosionMesh = new THREE.Mesh(explosionGeometry, explosionMaterial);
        explosionMesh.position.copy(this.position);
        this.scene.add(explosionMesh);
        
        // Animate explosion
        let explosionTime = 0;
        const maxTime = 0.3;
        const explosionInterval = setInterval(() => {
            explosionTime += 0.016; // ~60 FPS
            const progress = explosionTime / maxTime;
            
            explosionMesh.scale.setScalar(progress);
            explosionMaterial.opacity = 0.6 * (1 - progress);
            
            if (explosionTime >= maxTime) {
                this.scene.remove(explosionMesh);
                explosionGeometry.dispose();
                explosionMaterial.dispose();
                clearInterval(explosionInterval);
            }
        }, 16);
    }
    
    spawnExperience(enemy, gameState) {
        const expCount = enemy.expValue + Math.floor(Math.random() * 2); // Slight randomization
        
        for (let i = 0; i < expCount; i++) {
            const offsetX = enemy.position.x + (Math.random() - 0.5) * 3;
            const offsetY = enemy.position.z + (Math.random() - 0.5) * 3;
            const exp = new Experience(offsetX, offsetY, 1, this.scene);
            gameState.experiences.push(exp);
        }
    }
    
    spawnPowerup(enemy, gameState) {
        // Higher chance for powerups from special enemies
        let chance = 0.08; // Base 8% chance
        if (enemy.type === 'mini_boss') chance = 0.5;
        else if (enemy.type === 'elite' || enemy.type === 'tank') chance = 0.15;
        else if (enemy.type === 'splitter') chance = 0.12;
        
        if (Math.random() < chance) {
            const types = ['health', 'damage', 'speed'];
            const type = types[Math.floor(Math.random() * types.length)];
            const powerup = new Powerup(enemy.position.x, enemy.position.z, type, this.scene);
            gameState.powerups.push(powerup);
        }
    }
} 